import { type ReactNode, useState } from 'react';
import { Modal } from '@/components/ui/modal';
import { useSwapIg } from '@/hooks/use-ig-mutations';
import { IgReplaceBody } from './ig-replace-body';

export type ReplaceMode = 'replace_primary' | 'replace_secondary';

type Props = {
  open: boolean;
  mode: ReplaceMode | null;
  memberId: string;
  currentPrimary: string;
  onClose: () => void;
};

function normalize(v: string): string {
  return v.trim().replace(/^@/, '').toLowerCase();
}

function titleFor(mode: ReplaceMode): string {
  return mode === 'replace_primary'
    ? 'Cambiar IG principal'
    : 'Editar IG secundario';
}

function useReplaceForm(memberId: string, onClose: () => void) {
  const [value, setValue] = useState('');
  const [demote, setDemote] = useState(false);
  const swap = useSwapIg();
  const submit = (mode: ReplaceMode): void => {
    swap.mutate(
      { id: memberId, mode, new_value: normalize(value), demote_old: demote },
      {
        onSuccess: () => {
          setValue('');
          setDemote(false);
          onClose();
        },
      }
    );
  };
  return { value, setValue, demote, setDemote, swap, submit };
}

type FormHandle = ReturnType<typeof useReplaceForm>;

type BodyArgs = {
  mode: ReplaceMode;
  currentPrimary: string;
  f: FormHandle;
  onClose: () => void;
};

function bodyProps({ mode, currentPrimary, f, onClose }: BodyArgs) {
  return {
    mode,
    currentPrimary,
    value: f.value,
    setValue: f.setValue,
    demote: f.demote,
    setDemote: f.setDemote,
    pending: f.swap.isPending,
    canSubmit: !!normalize(f.value),
    onCancel: onClose,
    onConfirm: () => f.submit(mode),
  };
}

export function IgReplaceModal(p: Props): ReactNode {
  const f = useReplaceForm(p.memberId, p.onClose);
  if (!p.mode) return null;
  return (
    <Modal open={p.open} onClose={p.onClose} title={titleFor(p.mode)} size="sm">
      <IgReplaceBody
        {...bodyProps({
          mode: p.mode,
          currentPrimary: p.currentPrimary,
          f,
          onClose: p.onClose,
        })}
      />
    </Modal>
  );
}
